import { useRef, useState } from 'react'
import { usePage } from '@inertiajs/react'

import {
  fromTwelveHourClockParts,
  toCanonicalWallClock,
  usesTwelveHourClock,
  wallClockEntryPresentation,
  type DayPeriod,
  type TenantTimeFormatInput,
} from '@/shared/ui/wall-clock-input-utils'
import { t } from '@/shared/lib/i18n'
import { cn } from '@/shared/lib/utils'
import type { SharedProps } from '@/shared/types/SharedProps'
import { Input } from '@/shared/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select'

interface TenantTimeInputProps {
  id?: string
  /** Canonical wall-clock value `HH:mm` (00–23). Empty string when `allowEmpty` and cleared. */
  value: string
  onChange: (value: string) => void
  onBlur?: () => void
  disabled?: boolean
  className?: string
  'aria-invalid'?: boolean
  /** Override shared Inertia prop (tests / isolated previews). */
  tenantTimeFormat?: TenantTimeFormatInput
  /**
   * When true, clearing hour or minute commits `''` instead of coercing to `00:00`.
   * Defaults false so DateTimePicker nested consumers keep midnight coerce.
   */
  allowEmpty?: boolean
}

const clampHourTwentyFour = (hour: number): number => Math.min(23, Math.max(0, hour))
const clampHourTwelve = (hour: number): number => Math.min(12, Math.max(1, hour))
const clampMinute = (minute: number): number => Math.min(59, Math.max(0, minute))

/** Must stack above PopoverContent (`z-[200]` in popover.tsx) when nested in DateTimePickerInput. */
const PERIOD_SELECT_CONTENT_CLASS_NAME = 'z-[210]'

const parseDigitField = (text: string): number | null => {
  if (text === '') return null
  const parsed = Number(text)
  return Number.isFinite(parsed) ? parsed : null
}

/**
 * Tenant-aware wall-clock time entry. Presentation follows System Settings 12/24;
 * the emitted value is always canonical 24h `HH:mm` (midnight → `00:00`), or `''` when
 * `allowEmpty` and a field is cleared.
 */
const TenantTimeInput = ({
  id,
  value,
  onChange,
  onBlur,
  disabled,
  className,
  'aria-invalid': ariaInvalid,
  tenantTimeFormat: tenantTimeFormatProp,
  allowEmpty = false,
}: TenantTimeInputProps) => {
  const { tenantTimeFormat: sharedTenantTimeFormat } = usePage<SharedProps>().props
  const tenantTimeFormat = tenantTimeFormatProp ?? sharedTenantTimeFormat
  const isTwelveHour = usesTwelveHourClock(tenantTimeFormat)
  const presentation = wallClockEntryPresentation(value, tenantTimeFormat)

  // Draft text while a field is focused; otherwise derive from the canonical value.
  const [draftHourText, setDraftHourText] = useState<string | null>(null)
  const [draftMinuteText, setDraftMinuteText] = useState<string | null>(null)
  // Blur→focus (tab) can run in one turn before value '' re-renders; block stale presentation restore.
  const clearedToEmptyRef = useRef(false)
  // Empty presentation defaults period to AM; retain the last AM/PM across allowEmpty clear
  // so re-entry after clearing a PM time does not silently flip to morning.
  const [retainedPeriod, setRetainedPeriod] = useState<DayPeriod | null>(null)

  const hourText = draftHourText ?? presentation.hourText
  const minuteText = draftMinuteText ?? presentation.minuteText
  const period =
    value === '' && retainedPeriod != null ? retainedPeriod : (presentation.period ?? 'AM')
  const hourFieldEmpty = hourText === ''
  const minuteFieldEmpty = minuteText === ''
  const parsedHourDraft = parseDigitField(hourText)
  // Incomplete 12h drafts (e.g. "0") must not fall through to resolvedTwelveHour → invent noon.
  const twelveHourDraftInvalid =
    isTwelveHour &&
    hourText !== '' &&
    (parsedHourDraft == null || parsedHourDraft < 1 || parsedHourDraft > 12)
  // Tradeoff (REBUILD-369, accepted): empty commit uses OR (hour OR minute empty → `''`).
  // QA clears both fields; sequential clear (hour → tab → minute) must not coerce the empty
  // half to 0 and re-emit midnight before the second field is cleared. Live and blur share OR
  // so half-clear disables Save immediately. Accepted side effect: while bound value is `''`,
  // the non-empty sibling may still show draft digits (required fires; Save stays disabled).
  // Blanking the sibling would break sequential-clear recovery — keep draft digits by design.
  const shouldCommitEmpty =
    allowEmpty && (hourFieldEmpty || minuteFieldEmpty || twelveHourDraftInvalid)

  const emitCanonical = (hour: number, minute: number) => {
    clearedToEmptyRef.current = false
    setRetainedPeriod(null)
    onChange(toCanonicalWallClock(clampHourTwentyFour(hour), clampMinute(minute)))
  }

  const emitFromTwelve = (nextHour: number, nextMinute: number, nextPeriod: DayPeriod) => {
    const next = fromTwelveHourClockParts({
      hour: clampHourTwelve(nextHour),
      minute: clampMinute(nextMinute),
      period: nextPeriod,
    })
    emitCanonical(next.hour, next.minute)
  }

  const emitEmpty = (nextPeriod?: DayPeriod) => {
    clearedToEmptyRef.current = true
    if (isTwelveHour) {
      setRetainedPeriod(nextPeriod ?? period)
    }
    onChange('')
  }

  // Keep sibling digits as draft when live-clearing one field so the UI can still show them
  // while the bound value is `''` (Save blocked until both fields have digits again).
  const preserveSiblingDraftOnClear = (clearedField: 'hour' | 'minute') => {
    if (clearedField === 'hour' && draftMinuteText === null && minuteText !== '') {
      setDraftMinuteText(minuteText)
    }
    if (clearedField === 'minute' && draftHourText === null && hourText !== '') {
      setDraftHourText(hourText)
    }
  }

  // After clear→'', empty drafts stick on both fields. Typing into one field must drop the
  // sibling's empty draft so blur does not immediately re-commit '' (recovery would be impossible).
  const releaseEmptySiblingDraft = (changedField: 'hour' | 'minute') => {
    if (!allowEmpty) return
    if (changedField === 'hour' && draftMinuteText === '') {
      setDraftMinuteText(null)
    }
    if (changedField === 'minute' && draftHourText === '') {
      setDraftHourText(null)
    }
  }

  const resolvedMinute = (): number => {
    const parsedMinute = parseDigitField(minuteText)
    return parsedMinute == null ? 0 : clampMinute(parsedMinute)
  }

  const resolvedTwelveHour = (): number => {
    const parsedHour = parseDigitField(hourText)
    if (parsedHour != null && parsedHour >= 1 && parsedHour <= 12) {
      return clampHourTwelve(parsedHour)
    }
    // Incomplete/invalid drafts (e.g. "0") must not invent noon — keep the canonical hour.
    return clampHourTwelve(Number(presentation.hourText) || 12)
  }

  const resolvedTwentyFourHour = (): number => {
    const parsedHour = parseDigitField(hourText)
    return parsedHour == null ? 0 : clampHourTwentyFour(parsedHour)
  }

  const emptyDraftSeed = (): boolean => clearedToEmptyRef.current || (allowEmpty && value === '')

  const beginHourDraft = () => {
    // Keep an existing draft (e.g. preserved sibling digits after half-clear → `''`).
    if (draftHourText !== null) return
    setDraftHourText(emptyDraftSeed() ? '' : hourText)
  }

  const beginMinuteDraft = () => {
    if (draftMinuteText !== null) return
    setDraftMinuteText(emptyDraftSeed() ? '' : minuteText)
  }

  const commitDraft = () => {
    if (shouldCommitEmpty) {
      emitEmpty()
      // Preserve typed digits on the non-empty half so hour→tab→minute re-entry is not wiped.
      setDraftHourText(hourFieldEmpty ? '' : hourText)
      setDraftMinuteText(minuteFieldEmpty ? '' : minuteText)
    } else if (isTwelveHour) {
      emitFromTwelve(resolvedTwelveHour(), resolvedMinute(), period)
      setDraftHourText(null)
      setDraftMinuteText(null)
    } else {
      emitCanonical(resolvedTwentyFourHour(), resolvedMinute())
      setDraftHourText(null)
      setDraftMinuteText(null)
    }
    onBlur?.()
  }

  return (
    <div
      className={cn('flex items-center gap-1.5', className)}
      data-tenant-time-mode={isTwelveHour ? '12' : '24'}
    >
      <Input
        id={id}
        type="text"
        inputMode="numeric"
        value={hourText}
        disabled={disabled}
        aria-invalid={ariaInvalid}
        aria-label={t('common.time.hourLabel')}
        maxLength={2}
        className="w-12 tabular-nums text-center"
        onFocus={beginHourDraft}
        onChange={(event) => {
          const digits = event.target.value.replace(/\D/g, '').slice(0, 2)
          setDraftHourText(digits)
          if (digits === '') {
            if (allowEmpty) {
              preserveSiblingDraftOnClear('hour')
              emitEmpty()
            }
            return
          }

          const parsedHour = Number(digits)
          if (!Number.isFinite(parsedHour)) return

          releaseEmptySiblingDraft('hour')

          // allowEmpty: never invent :00 for a missing minute — wait for both fields.
          if (allowEmpty && parseDigitField(minuteText) == null) return

          if (isTwelveHour) {
            // Do not emit while the draft is out of 1–12 (e.g. typing "0" en route to "09").
            if (parsedHour < 1 || parsedHour > 12) return
            emitFromTwelve(parsedHour, resolvedMinute(), period)
          } else {
            emitCanonical(parsedHour, resolvedMinute())
          }
        }}
        onBlur={commitDraft}
      />
      <span className="text-sm font-medium text-muted-foreground" aria-hidden>
        :
      </span>
      <Input
        type="text"
        inputMode="numeric"
        value={minuteText}
        disabled={disabled}
        aria-invalid={ariaInvalid}
        aria-label={t('common.time.minuteLabel')}
        maxLength={2}
        className="w-12 tabular-nums text-center"
        onFocus={beginMinuteDraft}
        onChange={(event) => {
          const digits = event.target.value.replace(/\D/g, '').slice(0, 2)
          setDraftMinuteText(digits)
          if (digits === '') {
            if (allowEmpty) {
              preserveSiblingDraftOnClear('minute')
              emitEmpty()
            }
            return
          }

          const parsedMinute = Number(digits)
          if (!Number.isFinite(parsedMinute)) return

          releaseEmptySiblingDraft('minute')

          if (allowEmpty && parseDigitField(hourText) == null) return

          if (isTwelveHour) {
            emitFromTwelve(resolvedTwelveHour(), parsedMinute, period)
          } else {
            emitCanonical(resolvedTwentyFourHour(), parsedMinute)
          }
        }}
        onBlur={commitDraft}
      />
      {isTwelveHour && (
        <Select
          value={period}
          disabled={disabled}
          onValueChange={(selectedPeriod) => {
            const nextPeriod = selectedPeriod as DayPeriod
            if (shouldCommitEmpty) {
              emitEmpty(nextPeriod)
              return
            }
            emitFromTwelve(resolvedTwelveHour(), resolvedMinute(), nextPeriod)
          }}
        >
          <SelectTrigger
            size="sm"
            className="w-[4.5rem]"
            aria-invalid={ariaInvalid}
            aria-label={t('common.time.periodLabel')}
            onBlur={onBlur}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent className={PERIOD_SELECT_CONTENT_CLASS_NAME}>
            <SelectItem value="AM">{t('common.time.am')}</SelectItem>
            <SelectItem value="PM">{t('common.time.pm')}</SelectItem>
          </SelectContent>
        </Select>
      )}
    </div>
  )
}

export default TenantTimeInput
